import { Component, inject, OnDestroy } from "@angular/core";
import { BaseComponent } from "src/ancestors/base/base.component";
import { TokenStorageService } from "../../global-service/token-storage.service";
import { clearClientSession } from "../../global-service/session-cleanup";
import { EnvironmentLoaderService } from "src/ancestors/env-config.service";
import { UserService } from "../../global-service/user.service";
import { map } from "rxjs/operators";
import { TenantStore, AreaSelectionStore, TenantSwitchingStore } from "src/app/state";
import { TenantService } from "src/app/global-service/tenant.service";
import { AuthEventsService } from "src/app/global-service/auth-events.service";
import { PushNotificationService } from "src/app/global-service/push-notification.service";
import { isOnInstalledPWA } from "src/app/global-service/utils";
import { NAV_GROUP_CONFIGURATION, NAV_GROUP_MAIN, NAV_GROUP_MAINTENANCE, NAV_GROUP_SETTINGS } from "../utils/navigation-groups";

type NavigationGroupItem = { title: string; links: NavigationLink[], color: string };
type NavigationLink = { label: string; route: string; icon: string; action?: () => Promise<void>; enabled?: boolean, tooltip?: string };
@Component({
  selector: "app-layout",
  templateUrl: "./layout.component.html",
  styleUrls: ["./layout.component.scss"],
  standalone: false
})
export class LayoutComponent extends BaseComponent implements OnDestroy {
  private tkStorage: TokenStorageService = inject(TokenStorageService);
  protected envConfig: EnvironmentLoaderService = inject(EnvironmentLoaderService);
  private userService: UserService = inject(UserService);
  private tenantStore = inject(TenantStore);
  private areaSelectionStore = inject(AreaSelectionStore);
  private tenantSwitchingStore = inject(TenantSwitchingStore);
  private tenantService = inject(TenantService);
  private authEventsService = inject(AuthEventsService);
  private pushNotificationService = inject(PushNotificationService);
  protected tenantTitle: string = "";
  protected isSidebarCollapsed: boolean = false;
  protected isMobile: boolean = false;
  private resizeListener: () => void;
  private totalAreas: number = 0;
  protected userProfile: { userId: string, email: string, firstName: string, lastName: string, role: string, avatarImage: string | null } | null = null;

  // Area selection observables
  protected areaOptions$ = this.areaSelectionStore.areaOptions$.pipe(
    map(options => options.map(option =>
      option.value === null
        ? { ...option, label: this.translateService.instant<string>("generic.all_areas") }
        : option
    ))
  );
  protected selectedAreaId$ = this.areaSelectionStore.selectedAreaId$;

  // Tenant switching observables
  protected tenantOptions$ = this.tenantSwitchingStore.tenantOptions$;
  protected selectedTenantId$ = this.tenantSwitchingStore.selectedTenantId$;
  protected isSwitchingTenant$ = this.tenantSwitchingStore.isSwitching$;

  protected navigationGroups: NavigationGroupItem[] = [
    {
      title: this.translateService.instant<string>(NAV_GROUP_MAIN.titleKey),
      color: NAV_GROUP_MAIN.color,
      links: []
    }
  ];

  /**
   * L'utente è referente di almeno un'area.
   *
   * Non è un permesso: è una proprietà del dato, e va quindi chiesta al server. Il menu si
   * costruisce prima (sincrono, nel costruttore) e si ricostruisce quando la risposta arriva:
   * l'alternativa sarebbe rendere asincrona la costruzione di tutto il menu per una sola voce.
   */
  private isAreaReferent: boolean = false;

  private setupNavigationGroupsByUserProfile() {
    // ricostruzione ripetibile: si riparte dal solo gruppo principale, altrimenti una seconda
    // chiamata duplicherebbe i gruppi già aggiunti
    this.navigationGroups = [
      {
        title: this.translateService.instant<string>(NAV_GROUP_MAIN.titleKey),
        color: NAV_GROUP_MAIN.color,
        links: []
      }
    ];
    /*
     * Il menu si decide per PERMESSO, non per nome di profilo.
     *
     * Prima ogni voce nasceva da un `isAdmin || isTechnician || ...`, mentre la route guard
     * (`app-routing.module.ts`) decide per azione: le due cose divergevano, e la divergenza si
     * vedeva in due modi opposti. Da un lato voci nascoste ma raggiungibili digitando l'URL — era
     * il caso di `/organizations`, assente dal menu per il Manutentore ma aperta dalla guardia,
     * perche' possedeva `READ_ORGANIZATION_LIST` solo per far funzionare una tendina. Dall'altro
     * voci mostrate e poi respinte, come `/organizations` per il Tecnico se un giorno perdesse
     * quella AUTH. Testando la STESSA azione della guardia, i due lati non possono piu' divergere.
     *
     * E' anche la regola del progetto: mai decidere un'autorizzazione dal nome del profilo.
     */
    const hasTicketSection = this.userService.canReadTicketList();
    const hasFacilitySection = this.userService.canReadFacilityList();
    const hasOrganizationSection = this.userService.canReadOrganizationList();
    const hasMaintenanceContractSection = this.userService.canReadMaintenanceContractList();
    const hasInternalMaintenanceSection = this.userService.canReadInternalMaintenanceList();
    const hasUserSection = this.userService.canReadUserList();
    const hasAreaSection = this.userService.canReadAreaList();

    if (hasTicketSection) {
      // Overview e Dashboard non hanno guardia di rotta: `/homepage` e' la pagina su cui si atterra
      // dopo il login, quindi il menu non deve pretendere un permesso che la rotta non chiede. Prima
      // erano legate ai ticket, e un Amministrativo si trovava senza alcun link alla propria home.
      this.navigationGroups[0].links.push(
        {
          label: this.translateService.instant<string>("navigation.overview"),
          route: "/homepage",
          icon: "chartPie"
        }
      );
      this.navigationGroups[0].links.push(
        {
          label: this.translateService.instant<string>("navigation.dashboard"),
          route: "/dashboard",
          icon: "chartColumnBig",
          enabled: false,
          tooltip: this.translateService.instant<string>("dashboard.tooltip")
        }
      );
      this.navigationGroups[0].links.push(
        {
          label: this.translateService.instant<string>("navigation.tickets"),
          route: "/tickets",
          icon: "bug"
        }
      );
    }
    if (hasFacilitySection) {
      this.navigationGroups[0].links.push(
        {
          label: this.translateService.instant<string>("navigation.facilities"),
          route: "/facilities",
          icon: "factory"
        }
      );
    }
    if (hasOrganizationSection) {
      this.navigationGroups[0].links.push(
        {
          label: this.translateService.instant<string>("navigation.organizations"),
          route: "/organizations",
          icon: "gitFork"
        }
      );
    }
    /*
     * Gruppo MANUTENZIONI: le sue voci non nascono più tutte dalla stessa condizione.
     *
     * Le "Checklist condivise" non dipendono da un permesso ma dall'essere responsabile di
     * almeno una organizzazione, che è una proprietà del dato e non del ruolo (lo dice
     * esplicitamente l'analisi Milestone 3). Il gruppo va quindi creato se serve ALMENO una
     * delle due voci: legarlo al solo contratto avrebbe nascosto la pagina a un responsabile
     * che non ha accesso ai contratti, che è esattamente il caso d'uso.
     */
    const maintenanceLinks: NavigationLink[] = [];
    if (hasMaintenanceContractSection) {
      maintenanceLinks.push({
        label: this.translateService.instant<string>("navigation.maintenance-contracts"),
        route: "/maintenance-contracts",
        icon: "scrollText"
      });
    }
    /*
     * Le manutenzioni interne sono la stessa entità dei contratti con un perimetro diverso: la voce
     * si decide per PERMESSO come tutte le altre, e `canReadInternalMaintenanceList` compone
     * l'azione con il vincolo di contesto (mai Fornitore) — lo stesso che applicano la guardia
     * `InternalMaintenanceGuardFn` sulla rotta e il backend sul dato. Le tre decisioni vengono
     * dalla stessa funzione, quindi non possono divergere.
     */
    if (hasInternalMaintenanceSection) {
      maintenanceLinks.push({
        label: this.translateService.instant<string>("navigation.internal-maintenances"),
        route: "/internal-maintenances",
        icon: "wrench"
      });
    }
    // la guardia della rotta chiede `VIEW_SHARED_CHECKLISTS`, l'analisi chiede di essere
    // referente: servono entrambe, altrimenti la voce porta a /403
    if (this.isAreaReferent && this.userService.canViewSharedChecklists()) {
      maintenanceLinks.push({
        label: this.translateService.instant<string>("navigation.shared-checklists"),
        route: "/shared-checklists",
        icon: "clipboardList"
      });
    }
    if (maintenanceLinks.length > 0) {
      this.navigationGroups.push({
        title: this.translateService.instant<string>(NAV_GROUP_MAINTENANCE.titleKey),
        color: NAV_GROUP_MAINTENANCE.color,
        links: maintenanceLinks
      });
    }
    /*
     * I "Tipi" (impianto e componente) sono configurazione dell'anagrafica: la pagina non si limita
     * a mostrarli, li crea e li modifica da una sidenav, e quelle scritture chiedono
     * CREATE_FACILITY/UPDATE_FACILITY. Il menu la dava al solo SuperAdmin mentre la guardia della
     * rotta chiede `READ_FACILITY`, che hanno sei profili: la voce era nascosta e la pagina
     * raggiungibile a mano. `canWriteFacility()` e' il predicato scritto per questo caso — lo dice
     * il suo stesso JSDoc — e va tenuto allineato alla guardia in `app-routing.module.ts`.
     *
     * ATTENZIONE: cosi' la voce diventa visibile anche ad Admin, Tecnico e Compilatore. Se
     * l'intento del cliente e' davvero "solo SuperAdmin", allora e' la GUARDIA a essere sbagliata e
     * serve una azione dedicata (es. MANAGE_FACILITY_TYPES) su guardia e controller: da concordare.
     */
    const canReadTypes = this.userService.canWriteFacility();
    {
      const navGroup = {
        title: this.translateService.instant<string>(NAV_GROUP_CONFIGURATION.titleKey),
        links: [] as NavigationLink[],
        color: NAV_GROUP_CONFIGURATION.color
      };
      if (hasAreaSection) {
        navGroup.links.push(
          {
            label: this.translateService.instant<string>("navigation.areas"),
            route: "/areas",
            icon: "area"
          }
        );
      }
      if (canReadTypes) {
        navGroup.links.push(
          {
            label: this.translateService.instant<string>("navigation.types"),
            route: "/types",
            icon: "layers"
          }
        );
      }
      if (hasUserSection) {
        navGroup.links.push(
          {
            label: this.translateService.instant<string>("navigation.users"),
            route: "/users",
            icon: "users"
          }
        );
      }
      // come per MANUTENZIONI: il gruppo esiste se ha voci, senza un composito da tenere
      // allineato a mano a ogni cambio di permesso
      if (navGroup.links.length > 0) {
        this.navigationGroups.push(navGroup);
      }
    }

    this.navigationGroups.push({
      title: this.translateService.instant<string>(NAV_GROUP_SETTINGS.titleKey),
      color: NAV_GROUP_SETTINGS.color,
      links: [
        {
          label: this.translateService.instant<string>("navigation.account"),
          route: "/account",
          icon: "account"
        },
        {
          label: this.translateService.instant<string>("navigation.logout"),
          route: "/login",
          action: () => this.doLogout(),
          icon: "logout"
        }
      ]
    });

    // rimuovo le sezioni senza alcun link
    this.navigationGroups = this.navigationGroups.filter(n => n.links && n.links.length > 0);
  }

  /**
   * Verifica se l'utente è referente di un'area e, se lo è, rifà il menu.
   * Un errore non è fatale: la voce non compare, e la pagina resta comunque protetta perché
   * ricontrolla la condizione al proprio avvio.
   */
  private async loadAreaReferentFlag(): Promise<void> {
    try {
      const areas = await this.backendService.getAreasReferredByMe();
      if (areas.length === 0) {
        return;
      }
      this.isAreaReferent = true;
      this.setupNavigationGroupsByUserProfile();
    } catch {
      this.isAreaReferent = false;
    }
  }

  constructor() {
    super();
    this.logger.setCaller("LayoutComponent");
    this.setupNavigationGroupsByUserProfile();
    void this.loadAreaReferentFlag();
    this.checkScreenSize();

    // Create bound resize listener
    this.resizeListener = () => {
      this.checkScreenSize();
    };
    this.tenantTitle = this.tenantStore.currentState.tenant?.title ?? "";

    // Load areas for the area selector
    void this.loadAreas();
    void this.getUserProfile();

    // Load available tenants for tenant switching if user has permission
    if (this.userService.canChangeTenant()) {
      void this.loadAvailableTenants();
    }

    // Listen to window resize events
    window.addEventListener("resize", this.resizeListener);
  }

  override async ngOnDestroy(): Promise<void> {
    // Clean up event listener
    window.removeEventListener("resize", this.resizeListener);
    await super.ngOnDestroy();
  }

  private checkScreenSize(): void {
    this.isMobile = this.isMobileDevice();
    if (this.isMobile) {
      this.isSidebarCollapsed = true;
    } else {
      this.isSidebarCollapsed = false;
    }
  }

  private isMobileDevice(): boolean {
    // Check if screen size is mobile (considering both orientations)
    const maxMobileWidth = 768;
    const maxMobileHeight = 768;

    // Mobile if either dimension is <= 768px (covers both portrait and landscape)
    const isMobileSize = window.innerWidth <= maxMobileWidth || window.innerHeight <= maxMobileHeight;

    // Additional check for touch devices
    const isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0;

    return isMobileSize && isTouchDevice;
  }

  protected toggleSidebar(): void {
    this.isSidebarCollapsed = !this.isSidebarCollapsed;
  }

  protected onNavigationLinkClicked(): void {
    // Close sidebar on mobile when navigation link is clicked
    if (this.isMobile && !this.isSidebarCollapsed) {
      this.isSidebarCollapsed = true;
    }
  }

  private initializeUserState(): { userProfiles: string[], userAuth: string[] } {
    try {
      const jwtPayload = this.userService.getJwtPayload();
      if (jwtPayload?.user) {
        // If we have a valid JWT payload, update the state
        // const loginInfo = { jwtPayload: jwtPayload, expireDate: this.userService.getTokenExpireDate() };
        const userAuth = jwtPayload.auths;
        return { userProfiles: jwtPayload.profiles ?? [], userAuth: userAuth ?? [] };
      }
      return { userProfiles: [], userAuth: [] };
    } catch (error) {
      this.logger.warn("Could not initialize user state from JWT payload");
      return { userProfiles: [], userAuth: [] };
    }
  }

  protected async doLogout() {
    try {
      // Va letto PRIMA della pulizia, che rimuove anche questa chiave
      const redirectToLogin: string = sessionStorage.getItem("SSO_LOGIN") ? "Saml-sso/logoutSaml" : this.env.loginUrl;
      // Pulisco tutto lo stato locale della sessione: token, area selezionata, lingua, flag di
      // primo accesso. È la stessa funzione usata dal cambio tenant e dalla gestione di una
      // sessione non più valida — erano tre pulizie scritte in tre modi diversi, e nessuna
      // rimuoveva le stesse chiavi delle altre.
      const env = this.envConfig.getEnvConfig();
      const tokenKey = env.loginType === "local" ? env.localTokenKey : env.ssoTokenKey;
      clearClientSession(this.tkStorage, tokenKey);

      // Lo store tiene l'area anche in memoria: va allineato al `localStorage` appena ripulito,
      // altrimenti conserva la selezione del precedente utente fino al reload.
      this.areaSelectionStore.dispatchClearSelectedArea();

      // Unsubscribe from push notifications before logout
      try {
        if (this.pushNotificationService.isSubscribed) {
          await this.pushNotificationService.unsubscribeFromNotifications();
        }
      } catch (error) {
        this.logger.warn("[LayoutComponent] Failed to unsubscribe from push notifications during logout:" + (error as Error).message);
        // Continue with logout even if unsubscribe fails
      }

      // Emit logout completed event for push notifications cleanup
      this.authEventsService.emitLogoutCompleted();

      // Torno alla pagina di login
      await this.backendService.logout(redirectToLogin);
      // E ricarico la pagina
      window.location.reload();
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
    }
  }

  protected onUserAreaChange(areaId: unknown) {
    if (areaId === null || typeof areaId === "string") {
      // Update the selected area in the store (this will automatically save to localStorage)
      this.areaSelectionStore.dispatchSetSelectedArea(areaId);
    }
  }

  private async loadAreas(): Promise<void> {
    try {
      this.areaSelectionStore.dispatchSetLoading(true);
      const areas = await this.backendService.getAreas();
      this.areaSelectionStore.dispatchSetAreas(areas);
      this.totalAreas = areas.length;
      // if the user is not admin, and there is only one area, select it.
      // NON e' un'autorizzazione, quindi resta un test sul profilo: e' la comodita' di
      // preselezionare l'unica area a chi ne ha una sola, mentre a un admin la selezione va
      // lasciata esplicita. Non convertire in un controllo di permesso.
      if (!this.userService.isAdminOrSuperAdmin() && areas.length === 1) {
        this.areaSelectionStore.dispatchSetSelectedArea(areas[0].areaId);
      }
    } catch (error) {
      this.logger.error("Error loading areas:", error instanceof Error ? error.message : "Unknown error");
    } finally {
      this.areaSelectionStore.dispatchSetLoading(false);
    }
  }

  protected isAreaSelectVisible(): boolean {
    const path = this.router.url.split("?")[0]?.split("#")[0] ?? "";
    if (path === "/areas" || path.startsWith("/areas/")) {
      return false;
    }
    // come sopra: euristica di presentazione, non permesso. Il selettore serve a chi ha piu' di
    // un'area, e all'admin sempre, perche' il suo perimetro puo' cambiare senza ricaricare il menu.
    return this.userService.isAdminOrSuperAdmin() || this.totalAreas > 1;
  }

  protected isTenantSelectVisible(): boolean {
    const isPWA = isOnInstalledPWA();
    if (isPWA) {
      // in PWA we do not permit to change tenant because this will change the context of the PWA
      return false;
    }
    return this.userService.canChangeTenant() && this.tenantSwitchingStore.hasMultipleTenants;
  }

  protected get contextType() {
    return this.userService.getUserContextType() ?? undefined;
  }

  private async loadAvailableTenants(): Promise<void> {
    try {
      await this.tenantService.loadAvailableTenants();
      // Set the current tenant as selected based on the current tenant store
      const currentTenant = this.tenantStore.currentTenant;
      if (currentTenant) {
        // Find the tenant ID from the available tenants list
        const availableTenants = this.tenantSwitchingStore.availableTenants;
        const currentTenantData = availableTenants.find(t => t.name === currentTenant.name);
        if (currentTenantData) {
          this.tenantSwitchingStore.dispatchSetSelectedTenant(currentTenantData.tenantId);
        }
      }
    } catch (error) {
      this.logger.error("Error loading available tenants:", error instanceof Error ? error.message : "Unknown error");
    }
  }

  protected onTenantChange(tenantId: unknown): void {
    if (typeof tenantId === "string" && tenantId !== this.tenantSwitchingStore.selectedTenantId) {
      void this.performTenantSwitch(tenantId);
    }
  }

  private async performTenantSwitch(tenantId: string): Promise<void> {
    try {
      await this.tenantService.switchTenant(tenantId);
    } catch (error) {
      this.errorHandler.handleError(error);
      // Reset the selection back to the current tenant
      const currentTenant = this.tenantStore.currentTenant;
      if (currentTenant) {
        const availableTenants = this.tenantSwitchingStore.availableTenants;
        const currentTenantData = availableTenants.find(t => t.name === currentTenant.name);
        if (currentTenantData) {
          this.tenantSwitchingStore.dispatchSetSelectedTenant(currentTenantData.tenantId);
        }
      }
    }
  }

  protected async getUserProfile() {
    // load user options
    this.userProfile = {
      ...this.userService.getUserProfile(),
      avatarImage: null
    } as { userId: string, email: string, firstName: string, lastName: string, role: string, avatarImage: string | null };
    if (this.userProfile?.userId) {
      const accountDetails = await this.backendService.getAccountDetails();
      if (accountDetails?.userOptions?.avatarImage) {
        this.userProfile.avatarImage = accountDetails?.userOptions?.avatarImage ?? null;
      }
    }
  }

} 